import {
  CircleNotchIcon,
} from '@phosphor-icons/react'
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  isLoading?: boolean;
  loadingText?: string;
  icon?: ReactNode;
  iconClassName?: string;
  closeOnConfirm?: boolean;
  closeOnCancel?: boolean;
  showConfirmButton?: boolean;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  confirmClassName?: string;
  cancelClassName?: string;
  contentClassName?: string;
  descriptionClassName?: string;
  variant?: 'default' | 'destructive';
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  loadingText = 'Loading...',
  icon,
  iconClassName,
  closeOnConfirm = true,
  closeOnCancel = true,
  showConfirmButton = true,
  confirmDisabled = false,
  cancelDisabled = false,
  confirmClassName,
  cancelClassName,
  contentClassName,
  descriptionClassName,
  variant = 'default'
}: ConfirmDialogProps) {
  const handleConfirm = async () => {
    await onConfirm();
    if (closeOnConfirm) {
      onOpenChange(false);
    }
  };

  const handleCancel = async () => {
    if (onCancel) {
      await onCancel();
    }
    if (closeOnCancel) {
      onOpenChange(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={contentClassName}>
        <AlertDialogHeader>
          {icon ? (
            <div className="flex items-center gap-3">
              <div className={iconClassName}>
                {icon}
              </div>
              <AlertDialogTitle>{title}</AlertDialogTitle>
            </div>
          ) : (
            <AlertDialogTitle>{title}</AlertDialogTitle>
          )}
          {typeof description === 'string' ? (
            <AlertDialogDescription
              className={cn(icon && 'pl-13', descriptionClassName)}
            >
              {description}
            </AlertDialogDescription>
          ) : (
            <AlertDialogDescription
              asChild
              className={cn(icon && 'pl-13', descriptionClassName)}
            >
              <div>{description}</div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleCancel}
            disabled={isLoading || cancelDisabled}
            className={cancelClassName}
          >
            {cancelText}
          </AlertDialogCancel>
          {showConfirmButton ? (
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isLoading || confirmDisabled}
              className={cn(
                variant === 'destructive' && 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
                confirmClassName
              )}
            >
              {isLoading ? (
                <>
                  <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                  {loadingText}
                </>
              ) : (
                confirmText
              )}
            </AlertDialogAction>
          ) : null}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
} 
